import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { smbGenerationViewSchema, type SmbGenerationView } from "@shared/smb-generation";
import { apiRequest, parseJsonResponse } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";

const generationUrl = (id: string) => `/api/admin/smb/orders/${encodeURIComponent(id)}/generation`;
const toolNames: Record<string, string> = {
  ads_express: "Static ads",
  meme: "Meme",
  slideshow: "Slideshow",
  ugc: "UGC",
};

function readable(value: string) {
  return value.replaceAll("_", " ").replace(/\s+/g, " ").trim() || "Status not reported";
}

function taskState(value: string) {
  if (value === "pending") return "Waiting";
  if (value === "generating" || value === "running") return "Running";
  if (value === "ready" || value === "completed") return "Ready";
  if (value === "failed") return "Failed";
  return readable(value);
}

function TaskGroups({ view }: { view: SmbGenerationView }) {
  const personaNames = new Map(view.personas.map(persona => [persona.id, persona.name]));
  const groups = view.personas.map(persona => ({
    key: persona.id,
    name: persona.name,
    items: view.items.filter(item => item.personaId === persona.id),
  }));
  const otherItems = view.items.filter(item => !personaNames.has(item.personaId));
  if (otherItems.length) groups.push({ key: "other", name: "Other outputs", items: otherItems });

  if (view.items.length === 0) {
    return <p className="rounded-lg border border-dashed px-4 py-5 text-sm text-muted-foreground">
      Waiting for task details. No task total or progress percentage is available yet.
    </p>;
  }

  return <div className="space-y-3">
    {groups.filter(group => group.items.length > 0).map(group => <section key={group.key} className="overflow-hidden rounded-lg border">
      <h3 className="border-b bg-muted/35 px-3 py-2 text-sm font-medium">{group.name}</h3>
      <ul className="divide-y">
        {group.items.map((item, index) => {
          const tool = toolNames[item.tool] ?? readable(item.tool);
          return <li key={`${group.key}-${index}`} className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2.5 text-sm">
            <span className="min-w-0 truncate">{tool}</span>
            <span className="flex shrink-0 items-center gap-3 text-xs">
              <span className="capitalize text-muted-foreground">{taskState(item.state)}</span>
              <span className="tabular-nums text-muted-foreground" aria-label={`${item.assets.length} assets`}>
                {item.assets.length} {item.assets.length === 1 ? "asset" : "assets"}
              </span>
            </span>
            {item.state === "failed" && item.assets.length > 0 &&
              <span className="basis-full text-xs text-muted-foreground">This task failed; any listed assets are partial and are not a successful result.</span>}
          </li>;
        })}
      </ul>
    </section>)}
  </div>;
}

export function GenerationProgress({ orderId }: { orderId: string }) {
  const url = generationUrl(orderId);
  const query = useQuery<SmbGenerationView>({
    queryKey: [url],
    queryFn: async () => smbGenerationViewSchema.parse(await parseJsonResponse(await apiRequest("GET", url))),
    refetchInterval: current => current.state.data && !current.state.data.terminal ? 5000 : false,
  });
  const view = query.data;

  if (!view) {
    return <section className="space-y-3 rounded-xl border bg-card p-4" aria-labelledby="generation-progress-title">
      <div>
        <h3 id="generation-progress-title" className="text-sm font-semibold">Generation progress</h3>
        <p className="mt-1 text-sm text-muted-foreground">Task details have not arrived yet. No progress percentage is available.</p>
      </div>
      {query.isError && <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
        Progress could not be loaded. Your request was not changed; refresh to try the status check again.
      </p>}
      <Button size="sm" variant="outline" disabled={query.isFetching} onClick={() => void query.refetch()}>
        {query.isFetching ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
        Refresh progress
      </Button>
    </section>;
  }

  const total = view.counts.pending + view.counts.generating + view.counts.ready + view.counts.failed;
  const settled = view.counts.ready + view.counts.failed;
  const hasRun = typeof view.runId === "string" && view.runId.trim().length > 0;
  const hasTaskTotal = hasRun && total > 0;
  const percent = hasTaskTotal ? Math.round((settled / total) * 100) : null;
  const paused = view.status === "needs_review" || view.orderStatus === "needs_review";
  const stopped = view.terminal || ["failed", "cancelled"].includes(view.orderStatus);
  const live = !stopped && !paused;
  const statusLabel = readable(view.status);
  const holdMessage = paused
    ? "More work needs human review before admission. In-flight tasks may still finish. This view never starts or retries work."
    : stopped
      ? "Review the recorded outcomes; some tasks may be unfinished. Finished tasks still need human review."
      : view.counts.generating > 0
        ? "Generation is in progress. This page checks for updates automatically."
        : "Waiting for generation task updates. This page checks status without starting any work.";

  return <section className="space-y-4 rounded-xl border bg-card p-4 sm:p-5" aria-labelledby="generation-progress-title">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 id="generation-progress-title" className="text-sm font-semibold">Generation progress</h3>
        <p className="mt-1 text-sm text-muted-foreground">Status: <span className="font-medium capitalize text-foreground">{statusLabel}</span></p>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground" aria-live="polite">
        {live ? <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />Live updates</>
          : paused ? "Paused · review required" : "Status settled"}
      </div>
    </div>

    <p className="rounded-lg bg-muted/45 px-3 py-2.5 text-sm text-muted-foreground">{holdMessage}</p>

    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {([
        ["Ready", view.counts.ready],
        ["Running", view.counts.generating],
        ["Waiting", view.counts.pending],
        ["Failed", view.counts.failed],
      ] as const).map(([label, count]) => <div key={label} className="rounded-lg border bg-background/60 px-3 py-2">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 text-lg font-semibold tabular-nums">{count}</dd>
      </div>)}
    </dl>

    {hasTaskTotal ? <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium">Generation tasks finished</span>
        <span className="tabular-nums text-muted-foreground">{settled} of {total}</span>
      </div>
      <progress
        className="block h-2.5 w-full overflow-hidden rounded-full accent-primary"
        max={total}
        value={settled}
        role="progressbar"
        aria-label="Generation tasks finished"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={settled}
        aria-valuetext={`${settled} of ${total} tasks finished; ${view.counts.ready} ready and ${view.counts.failed} failed`}
      />
      <p className="text-xs text-muted-foreground">{percent}% of tasks have finished. Failed tasks are not successful results; all finished tasks still require human review.</p>
    </div> : <p className="rounded-lg border border-dashed px-3 py-3 text-sm text-muted-foreground">
      Waiting for task details. {!hasRun ? "No generation run is attached yet." : "A task total is not recorded."} No percentage is shown.
    </p>}

    {query.isError && <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
      Could not refresh progress. The last recorded snapshot remains visible; use Refresh progress to check again.
    </p>}

    <TaskGroups view={view} />
    <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
      <p className="text-xs text-muted-foreground">Assets are counted only. Completion does not approve creative or move the order forward.</p>
      <Button size="sm" variant="outline" disabled={query.isFetching} onClick={() => void query.refetch()}>
        {query.isFetching ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
        Refresh progress
      </Button>
    </div>
  </section>;
}
